import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Copy, GitCompareArrows, Scale } from 'lucide-react'

import { AppShell } from '../../components/AppShell'
import { ObjectPicker } from '../../components/ObjectPicker'
import {
  JsonChangeRow,
  JsonColumnShell,
  JsonPane,
  type JsonLineTone,
} from '../../components/JsonView'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePersistedState } from '../../hooks/usePersistedState'
import { LineItemsApi, type LineItem } from '@/services/lineItems/lineItemsApi'
import { searchLineItems } from '@/services/lineItems/lineItemSearch'
import { copyToClipboard } from '@/utils/clipboard'
import { diffJson, jsonLines, type JsonLine } from '@/utils/jsonDiff'

/** Which side of the comparison a pane is. */
type Side = 'left' | 'right'

/** The line item as the API returns it, for one side. */
function useRawLineItem(id: string | undefined) {
  return useQuery({
    queryKey: ['lineitem-raw', id],
    queryFn: () => LineItemsApi.getRawById(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

/**
 * Two line items as they stand, side by side in JSON with the fields that
 * differ lit up — version history's view of one object over time, turned
 * sideways to two objects at one moment.
 */
export function LineItemComparisonPage() {
  const { t } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)

  // Kept so a comparison survives a reload while it is being worked through.
  const [left, setLeft] = usePersistedState<LineItem | null>('lineItemComparison.left', null)
  const [right, setRight] = usePersistedState<LineItem | null>('lineItemComparison.right', null)

  const leftJson = useRawLineItem(left?.id)
  const rightJson = useRawLineItem(right?.id)
  const ready = !!leftJson.data && !!rightJson.data

  const changes = useMemo(
    () => (ready ? diffJson(leftJson.data, rightJson.data) : []),
    [ready, leftJson.data, rightJson.data]
  )

  /** The paths that differ, so each pane can light its own lines. */
  const changed = useMemo(() => new Map(changes.map((c) => [c.path, c])), [changes])

  /** Paths each side has, so a container only one of them holds stands out. */
  const paths = useMemo(
    () => ({
      left: new Set(ready ? jsonLines(leftJson.data).map((line) => line.path) : []),
      right: new Set(ready ? jsonLines(rightJson.data).map((line) => line.path) : []),
    }),
    [ready, leftJson.data, rightJson.data]
  )

  const toneFor =
    (side: Side) =>
    (line: JsonLine): JsonLineTone => {
      if (!ready) return null
      const other = side === 'left' ? paths.right : paths.left
      if (line.open || line.close) {
        return line.path && !other.has(line.path) ? (side === 'left' ? 'removed' : 'added') : null
      }
      const change = changed.get(line.path)
      if (!change) return null
      // A field only one side has reads as removed on the left, added on the right.
      if (change.before === undefined) return side === 'left' ? null : 'added'
      if (change.after === undefined) return side === 'left' ? 'removed' : null
      return 'changed'
    }

  /** Brings a differing line into view on both sides at once. */
  const reveal = (path: string) => {
    for (const side of ['left', 'right'] as Side[]) {
      scrollerRef.current
        ?.querySelector<HTMLElement>(
          `[data-json-pane="${side}"] [data-path="${CSS.escape(path)}"]`
        )
        ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    }
  }

  const picker = (side: Side, value: LineItem | null, onChange: (v: LineItem | null) => void) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          {t(side === 'left' ? 'lineItemComparison.first' : 'lineItemComparison.second')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* One line item at most: picking another replaces it. */}
        <ObjectPicker<LineItem>
          queryKey={`line-item-comparison-${side}`}
          search={searchLineItems}
          selected={value ? [value] : []}
          onChange={(next) => onChange(next.at(-1) ?? null)}
          addLabel={t('lineItemComparison.pick')}
          searchPlaceholder={t('lineItemComparison.search')}
          emptyText={t('lineItemComparison.none')}
          renderMeta={(lineItem) => lineItem.id}
        />
      </CardContent>
    </Card>
  )

  const pane = (side: Side, lineItem: LineItem | null, json: unknown) => (
    <JsonColumnShell
      className="min-w-0"
      header={
        <div className="flex items-center gap-2 text-sm">
          <span className="truncate font-medium">{lineItem?.name}</span>
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto"
            onClick={() => copyToClipboard(JSON.stringify(json, null, 2))}
            aria-label={t('common.copyToClipboard')}
          >
            <Copy />
          </Button>
        </div>
      }
    >
      <JsonPane value={json} paneId={side} tone={toneFor(side)} />
    </JsonColumnShell>
  )

  return (
    <AppShell
      crumbs={[
        { label: t('nav.superAdmin'), href: '/super-admin' },
        { label: t('lineItemComparison.title') },
      ]}
    >
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('lineItemComparison.title')}</h1>
            <p>{t('lineItemComparison.description')}</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {picker('left', left, setLeft)}
          {picker('right', right, setRight)}
        </div>

        {(leftJson.isLoading || rightJson.isLoading) && <Skeleton className="h-96 w-full" />}

        {!left || !right ? (
          <Card>
            <CardContent className="flex items-center gap-2 py-8 text-sm text-(--ad-text-muted)">
              <Scale className="size-4" />
              {t('lineItemComparison.pickBoth')}
            </CardContent>
          </Card>
        ) : (
          ready && (
            <div
              ref={scrollerRef}
              className="grid grid-cols-2 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem_minmax(0,1fr)]"
            >
              <div className="order-2 min-w-0 lg:order-1">
                {pane('left', left, leftJson.data)}
              </div>

              <JsonColumnShell
                className="order-1 col-span-2 min-w-0 border-dashed lg:order-2 lg:col-span-1"
                header={
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <GitCompareArrows className="size-4 text-(--ad-text-muted)" />
                    <span className="truncate">{left.name}</span>
                    <ArrowRight className="size-3.5 shrink-0 text-(--ad-text-muted)" />
                    <Badge variant="gray" className="ml-auto">
                      {t('versionHistory.fieldsChanged', { count: changes.length })}
                    </Badge>
                  </div>
                }
              >
                {changes.length === 0 && (
                  <p className="p-3 text-xs text-(--ad-text-muted)">
                    {t('lineItemComparison.identical')}
                  </p>
                )}
                {changes.map((change) => (
                  <JsonChangeRow
                    key={change.path}
                    change={change}
                    onClick={() => reveal(change.path)}
                  />
                ))}
              </JsonColumnShell>

              <div className="order-3 min-w-0">
                {pane('right', right, rightJson.data)}
              </div>
            </div>
          )
        )}
      </div>
    </AppShell>
  )
}
